import React, { useCallback, useEffect, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import HTMLRenderer from 'react-html-renderer'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'

import { useAppDispatch } from 'hooks/reduxHooks'
import { fillArray, min } from 'lib/arrayUtils'
import useToggle from 'hooks/useToggle'
import { fetchCabinRateDetails } from 'actions/CruiseActions'
import CabinRateDetails from './CruiseCabinRateDetails'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextButton from 'components/Luxkit/Button/TextButton'
import AspectRatio from 'components/utils/AspectRatio'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { ImageParams } from 'components/Common/ResponsiveImage'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import PaneDetailsSection from 'components/Common/Pane/PaneDetailsSection'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import FormatCurrency from 'components/Common/FormatCurrency'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import Pill from 'components/Luxkit/Pill'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CruiseDiscountTermsAndConditions from 'components/Cruises/CruiseDiscountTermsAndConditions'
import { fireInteractionEvent } from 'api/googleTagManager'
import { cruiseViewCabinCategoryRates } from 'analytics/eventDefinitions'

const Container = styled.div`
  padding: 0;
  width: 100%;
  margin: ${rem(20)} 0 ${rem(10)} 0;

  border-radius: ${props => props.theme.borderRadius.medium};
  background: ${props => props.theme.palette.neutral.eight};
  border: 1px solid ${props => props.theme.palette.neutral.six};

  > div div section.cabin-rate-section, section.cabin-type-details {
    border-top: 1px solid ${props => props.theme.palette.neutral.six};
  }

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(20)} 0 ${rem(20)};
  }
`

const GridContainer = styled.div`
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  padding-bottom: ${rem(20)};

  display: flex;
  gap: ${rem(20)};
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    > :nth-child(3) {
      padding-left: ${rem(16)};
      padding-right: 0;
      border-left: 1px solid ${props => props.theme.palette.neutral.six};
    }

    display: grid;
    grid-column-gap: ${rem(20)};
    grid-template-columns: 1.5fr 2fr 2.2fr;
  }
`

const GridColumn = styled.div`
  width: 100%;

  &.spacing-top {
    margin-top: ${rem(4)};
  }

  &.sm-up-flex-column {
    width: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;

    ${mediaQueryUp.tablet} {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  ${mediaQueryUp.desktop} {
    &:not(.full-width) {
      padding: 0 ${rem(8)};
    }
  }
`

const GridColumnHideOnSm = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
  }
`

const TileMediaContainer = styled(AspectRatio)`
  background-color: ${({ theme }) => theme.palette.neutral.seven};
`

const CabinDescription = styled(BodyText)`
  line-clamp: 3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  margin-top: ${rem(12)};
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;

  > p:not(:first-child) {
    display: none;
  }
`

const DetailsPane = styled.div`
  padding: ${rem(20)};

  > div:nth-last-child(-n + 1) {
    padding: 0;
    border: none;
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(20)} 0;
  }
`

const SpacingDescription = styled(BodyText)`
  margin-top: ${rem(8)};
`

const RateDetailsContainer = styled.div`
  margin-top: ${rem(24)};

  > div.rate-details-container:not(:last-child) {
    margin: ${rem(8)} 0;
    padding-bottom: ${rem(24)};
    border-bottom: 1px solid ${props => props.theme.palette.neutral.six};
  }
`

const ImageSkeleton = styled(LoadingBox)`
  width: 100%;
  min-height: ${rem(64)};
`

const ContentSkeleton = styled(LoadingBox)`
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.large};
  min-height: ${rem(10)};
  margin: 0 0 ${rem(8)} 0;
`

const Actions = styled.div`
  ${mediaQueryUp.tablet} {
    width: 100%;
  }
`

const MEDIA_IMAGE_PARAMS: ImageParams = {
  quality: 'eco',
  mobileAspectRatio: '3:2',
  tabletAspectRatio: '3:2',
  desktopAspectRatio: '3:2',
  largeDesktopAspectRatio: '3:2',
  mobileWidth: '480px',
  tabletWidth: '480px',
  desktopWidth: '150px',
  largeDesktopWidth: '150px',
}

const showableComponentIds = (
  cabinCode: string,
  rateDetails: App.CruiseCabinRateView[],
): Array<string> => {
  return rateDetails
    .filter(rate => rate.pricedCategoryCode === cabinCode)
    .map(item => item.componentId)
}

const getCabinCategory = (
  cabin: App.CruiseCabinInfo,
  rate: App.CruiseCabinRateView,
): App.CruiseSelectedCategory => ({
  imageId: cabin.imageId,
  category: rate.componentId,
  categoryName: rate.cabinType,
  categoryInfoName: cabin.categoryInfo?.name,
  rateDescription: rate.rateCodeDescription,
})

interface Props extends RouteComponentProps {
  id: string
  sessionId: string
  bookingId: string
  offer: App.CruiseOffer
  cabin: App.CruiseCabinInfo
  travelersInfo: Array<App.Occupants>
  cabinRates: Array<App.CruiseCabinRateView>
  cabinRateDetailsError: { [componentId: string]: boolean }
  cabinRateDetailsLoading: { [componentId: string]: boolean }
  cabinRateDetails: { [componentId: string]: App.CruiseCabinRateDetailsResponse }
}

function CabinOfferTile(props: Props) {
  const {
    id,
    cabin,
    cabinRates,
    cabinRateDetails,
    cabinRateDetailsError,
    cabinRateDetailsLoading,
    travelersInfo,
    bookingId,
  } = props

  const dispatch = useAppDispatch()
  const [showRateDetails, onToggleDetails] = useToggle()
  const [showMoreOpened, onToggleShowMore] = useToggle()

  const images = useMemo(() => [{ id: cabin.imageId }], [cabin.imageId])

  const onRateDetailsToggle = useCallback(() => {
    fireInteractionEvent(cruiseViewCabinCategoryRates(id))
    onToggleDetails()
  }, [id, onToggleDetails])

  const [
    lowestRatePriceByCabinCode,
    brochureInfo,
    rateDetails,
  ] = useMemo(() => {
    if (cabinRates) {
      const rateByCabinCode = cabinRates.filter(
        (rate) => rate.pricedCategoryCode === cabin.code)

      if (rateByCabinCode?.length) {
        const lowestRateByPrice = min(rateByCabinCode, code => code.ratePrices.price)
        return [
          Math.round(lowestRateByPrice.ratePrices.price),
          lowestRateByPrice.brochureInfo,
          rateByCabinCode,
        ]
      }
    }
  }, [cabin.code, cabinRates])

  useEffect(() => {
    if (showRateDetails && rateDetails?.length) {
      const ids = showableComponentIds(cabin.code, rateDetails)
      ids.forEach((id) => dispatch(fetchCabinRateDetails(id)))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateDetails, showRateDetails])

  return (
    <Container>
      <GridContainer>
        <GridColumn className="full-width">
          <TileMediaContainer mobileAspectRatio="3:2">
            <ImageCarousel
              eagerLoadFirstImage
              images={images}
              imageParams={MEDIA_IMAGE_PARAMS} />
          </TileMediaContainer>
        </GridColumn>

        <GridColumn>
          <Heading variant="heading4" as="span">{cabin.categoryInfo?.name}</Heading>
          <BodyText className="spacing-top" variant="small">Category code: <b>{cabin.code}</b></BodyText>
          {cabin.categoryInfo?.description && (
            <>
              <CabinDescription variant="small"><HTMLRenderer html={cabin.categoryInfo?.description}/></CabinDescription>
              <SmallToggleButton
                size="small"
                iconPosition="end"
                on={showMoreOpened}
                onClick={onToggleShowMore}
              >{showMoreOpened ? 'Read less' : 'Read more'}</SmallToggleButton>
            </>
          )}
        </GridColumn>

        <GridColumn className="sm-up-flex-column">
          <VerticalSpacer gap={8}>
            <div>
              <Caption variant="large" colour="neutral-three">From</Caption>
              <div>
                <Heading variant="heading5" as="span">
                  <FormatCurrency value={lowestRatePriceByCabinCode} format="dynamicDollar"/>
                </Heading>{' '}
                <Caption variant="large" colour="neutral-two" as="span">/adult</Caption>
              </div>
              <Caption variant="medium" colour="neutral-three">Includes taxes &amp; fees</Caption>
              {brochureInfo && (
                <ValueWrapper variant="large" weight="bold">
                  <BodyText variant="small" colour="neutral-one">
                    Valued up to <FormatCurrency value={Math.round(brochureInfo.price)}/>
                  </BodyText>
                  <Pill
                    kind="primary"
                    variant="success">
                    -{brochureInfo.discountPercentage}%</Pill>
                  <CruiseDiscountTermsAndConditions />
                </ValueWrapper>
              )}
            </div>
            <Actions>
              <TextButton
                size="medium"
                fit="full-width"
                kind="secondary"
                onClick={onRateDetailsToggle}
                endIcon={<DropdownChevron direction={showRateDetails ? 'up' : 'down'} />}
              >{showRateDetails ? 'Hide Rates' : 'View Rates'}</TextButton>
            </Actions>
          </VerticalSpacer>
        </GridColumn>
      </GridContainer>

      <PaneDetailsSection className="cabin-type-details" isOpen={showMoreOpened}>
        <DetailsPane>
          <Caption variant="large" weight="bold">About this cabin</Caption>
          <SpacingDescription variant="small">
            <HTMLRenderer html={cabin.categoryInfo?.description} />
          </SpacingDescription>
        </DetailsPane>
      </PaneDetailsSection>

      <PaneDetailsSection className="cabin-rate-section" isOpen={showRateDetails}>
        <DetailsPane>
          <Heading variant="heading5" as="span">Select your preferred rate</Heading>

          <RateDetailsContainer>
            {rateDetails?.map((rate) => (
              <CabinRateDetails
                {...props}
                bookingId={bookingId}
                cabinCode={cabin.code}
                rate={rate}
                key={rate.componentId}
                occupancy={travelersInfo}
                onboardCredits={rate.onboardCredits}
                rateTitle={rate.rateCodeDescription}
                ratePrice={Math.round(rate.ratePrices.price)}
                ratePrices={rate.ratePrices}
                isNccfIncluded={rate.ratePrices.isNccfIncluded}
                isTaxesIncluded={rate.ratePrices.isTaxesIncluded}
                tax={rate.ratePrices.taxes}
                cabinCategory={getCabinCategory(cabin, rate)}
                ratePromotionTitle={rate.applicablePromotion && rate.promotionTitle}
                cabinRateDetails={cabinRateDetails ? cabinRateDetails[rate.componentId] : null}
                cabinRateDetailsError={cabinRateDetailsError ? cabinRateDetailsError[rate.componentId] : null}
                cabinRateDetailsLoading={cabinRateDetailsLoading ? cabinRateDetailsLoading[rate.componentId] : null}
                cabinStatus={rate.status} />
            ))}
          </RateDetailsContainer>
        </DetailsPane>
      </PaneDetailsSection>
    </Container>
  )
}

function mapStateToProps(appState: App.State) {
  return {
    bookingId: appState.cruise.bookingId,
    cabinRateDetails: appState.cruise.cabinRateDetails,
    cabinRateDetailsError: appState.cruise.cabinRateDetailsError,
    cabinRateDetailsLoading: appState.cruise.cabinRateDetailsLoading,
  }
}

export default withRouter(connect(mapStateToProps)(CabinOfferTile))

export function CabinOfferTileSkeleton() {
  return (
    <>
      {fillArray(4).map((id) => (
        <Container key={id}>
          <GridContainer>
            <GridColumn className="full-width">
              <ImageSkeleton />
            </GridColumn>
            <GridColumn>
              {fillArray(4).map((id) => <ContentSkeleton key={id} />)}
            </GridColumn>
            <GridColumnHideOnSm>
              {fillArray(4).map((id) => <ContentSkeleton key={id} />)}
            </GridColumnHideOnSm>
          </GridContainer>
        </Container>
      ))}
    </>
  )
}
